/**
 * Questionnaire v2 — core (navigation, lazy loading, étape 1)
 * Page : /pages/devis — n'interfère pas avec l'ancien questionnaire.
 */
(function () {
  'use strict';

  const HOST_SEL = '[data-qv2-steps-host]';
  const CONFIG_SEL = '#qv2-config-data';
  const LOADING_SEL = '#qv2-step-loading';
  const WEDDING_DATE_STORAGE_KEY = 'qv2-wedding-date';

  let config = null;
  let currentStep = 1;
  let weddingDateMemory = '';
  const loadedSectionKeys = new Set();
  const loadedStepNumbers = new Set();

  const IMAGE_RETRY_DELAYS_MS = [1000, 3000, 8000];
  const IMAGE_MAX_CONCURRENT = 4;
  const FETCH_RETRY_DELAYS_MS = [800, 2000, 5000];
  const FETCH_MAX_ATTEMPTS = 3;

  let imageLoadQueue = [];
  let imageLoadActive = 0;

  function cleanImageUrl(url) {
    if (!url || typeof url !== 'string') return '';
    return url.trim()
      .replace(/&amp;/g, '&')
      .replace(/([&?])_qv2r=\d+/g, '$1')
      .replace(/([&?])_qrb=\d+/g, '$1')
      .replace(/\?&/, '?')
      .replace(/[?&]$/, '');
  }

  function getImageFallbackSrc(img) {
    if (!img) return '';
    return img.getAttribute('data-fallback-src') ||
      img.getAttribute('data-lightbox-src') ||
      img.getAttribute('data-src') ||
      img.getAttribute('src') || '';
  }

  function scheduleImageRetry(img, url) {
    const attempt = parseInt(img.dataset.qv2ImgAttempt || '0', 10);
    if (attempt >= IMAGE_RETRY_DELAYS_MS.length) return;
    const delay = IMAGE_RETRY_DELAYS_MS[attempt];
    img.dataset.qv2ImgAttempt = String(attempt + 1);
    setTimeout(function () {
      if (!img.isConnected) return;
      const clean = cleanImageUrl(url);
      if (!clean) return;
      const sep = clean.indexOf('?') >= 0 ? '&' : '?';
      img.src = clean + sep + '_qv2r=' + Date.now();
    }, delay);
  }

  function bindImageErrorRecovery(img) {
    if (!img || img.dataset.qv2ImgResilience === '1') return;
    img.dataset.qv2ImgResilience = '1';
    img.addEventListener('error', function () {
      if (img.naturalWidth > 0) return;
      const fallback = cleanImageUrl(getImageFallbackSrc(img));
      if (!fallback || fallback.indexOf('data:') === 0) return;
      scheduleImageRetry(img, fallback);
    });
    img.addEventListener('load', function () {
      if (img.naturalWidth > 0) {
        img.dataset.qv2ImgAttempt = '0';
      }
    });
  }

  function pumpImageQueue() {
    while (imageLoadActive < IMAGE_MAX_CONCURRENT && imageLoadQueue.length) {
      const job = imageLoadQueue.shift();
      imageLoadActive++;
      job(function done() {
        imageLoadActive--;
        pumpImageQueue();
      });
    }
  }

  function enqueueImageLoad(job) {
    imageLoadQueue.push(job);
    pumpImageQueue();
  }

  function applyImageSrc(img, url, lightboxUrl) {
    if (!img || !url || url.indexOf('data:') === 0) return;
    const clean = cleanImageUrl(url);
    if (!clean) return;
    img.loading = 'eager';
    img.classList.remove('lazyload', 'lazyloaded', 'lazyloading');
    img.removeAttribute('data-src');
    if (lightboxUrl) {
      img.setAttribute('data-lightbox-src', lightboxUrl);
      img.setAttribute('data-fallback-src', clean);
    } else if (!img.getAttribute('data-fallback-src')) {
      img.setAttribute('data-fallback-src', clean);
    }
    const currentBase = (img.getAttribute('src') || '').replace(/([&?])_qv2r=\d+/g, '$1').split('?')[0];
    const nextBase = clean.split('?')[0];
    if (currentBase === nextBase && img.complete && img.naturalWidth > 0) return;
    if (currentBase !== nextBase || (img.complete && img.naturalWidth === 0)) {
      img.dataset.qv2ImgAttempt = '0';
      img.src = clean;
    }
    bindImageErrorRecovery(img);
  }

  function forceLoadImage(img, url, lightboxUrl) {
    if (!img || !url) return;
    enqueueImageLoad(function (done) {
      applyImageSrc(img, url, lightboxUrl);
      if (img.complete) {
        done();
        return;
      }
      function onFinish() {
        img.removeEventListener('load', onFinish);
        img.removeEventListener('error', onFinish);
        done();
      }
      img.addEventListener('load', onFinish);
      img.addEventListener('error', onFinish);
    });
  }

  function fixBrokenImagesInStep(stepEl) {
    if (!stepEl) return;
    stepEl.querySelectorAll('img').forEach(function (img) {
      if (img.closest('video, .questionnaire-video')) return;
      const fallback = cleanImageUrl(getImageFallbackSrc(img));
      if (!fallback || fallback.indexOf('data:') === 0) return;
      bindImageErrorRecovery(img);
      const src = img.getAttribute('src') || '';
      if (!src || src.indexOf('data:') === 0 || (img.complete && img.naturalWidth === 0)) {
        forceLoadImage(img, fallback, img.getAttribute('data-lightbox-src') || fallback);
      }
    });
  }

  function hydrateStepImages(stepEl) {
    if (!stepEl) return;
    stepEl.querySelectorAll('img').forEach(function (img) {
      if (img.closest('video, .questionnaire-video')) return;
      const fallback = cleanImageUrl(getImageFallbackSrc(img));
      if (!fallback || fallback.indexOf('data:') === 0) return;
      img.loading = 'eager';
      bindImageErrorRecovery(img);
      if (!img.getAttribute('src') || (img.complete && img.naturalWidth === 0)) {
        forceLoadImage(img, fallback, img.getAttribute('data-lightbox-src') || fallback);
      }
    });
  }

  function scheduleStepImageHydration(stepEl, stepNumber) {
    if (!stepEl) return;

    function runHydration() {
      hydrateStepImages(stepEl);
      requestAnimationFrame(function () {
        fixBrokenImagesInStep(stepEl);
      });
      setTimeout(function () {
        fixBrokenImagesInStep(stepEl);
      }, 600);
      setTimeout(function () {
        fixBrokenImagesInStep(stepEl);
      }, 2500);
    }

    const video = stepEl.querySelector('.questionnaire-video');
    if (video && video.readyState < 3) {
      video.addEventListener('canplay', runHydration, { once: true });
      setTimeout(runHydration, stepNumber === 1 ? 8000 : 5000);
      return;
    }

    runHydration();
  }

  async function fetchWithRetry(url, attempts) {
    const maxAttempts = attempts || FETCH_MAX_ATTEMPTS;
    let lastError = null;
    for (let i = 0; i < maxAttempts; i++) {
      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response;
      } catch (err) {
        lastError = err;
        if (i < maxAttempts - 1) {
          await new Promise(function (resolve) {
            setTimeout(resolve, FETCH_RETRY_DELAYS_MS[i] || 2000);
          });
        }
      }
    }
    throw lastError || new Error('Fetch failed');
  }

  const VIDEO_RETRY_DELAYS_MS = [1000, 3000, 8000];
  const VIDEO_LOOKAHEAD = 4;
  const VIDEO_WARMUP_FROM_STEP = 3;
  const VIDEO_WARMUP_TO_STEP = 12;
  const VIDEO_WARMUP_QUEUE_TIMEOUT_MS = 15000;
  const prefetchedVideoUrls = new Set();
  let step1VideoReadyForWarmup = false;
  let videoWarmupQueue = [];
  let videoWarmupActive = false;

  function isSlowConnection() {
    const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!conn) return false;
    return !!(conn.saveData || conn.effectiveType === 'slow-2g' || conn.effectiveType === '2g');
  }

  function getVideoLookahead() {
    return isSlowConnection() ? 2 : VIDEO_LOOKAHEAD;
  }

  function getVideoWarmupToStep() {
    return isSlowConnection() ? 6 : VIDEO_WARMUP_TO_STEP;
  }

  function getTotalSteps() {
    return config?.totalSteps || 18;
  }

  function getStepElement(stepNumber) {
    const host = getHost();
    if (host) {
      const inHost = host.querySelector('.questionnaire-step[data-step="' + stepNumber + '"]');
      if (inHost) return inHost;
    }
    return document.querySelector('.questionnaire-step[data-step="' + stepNumber + '"]');
  }

  function getStepVideo(stepNumber) {
    const stepEl = getStepElement(stepNumber);
    return stepEl ? stepEl.querySelector('.questionnaire-video') : null;
  }

  function getQuestionnaireVideoPreloadUrl(video) {
    if (!video) return '';
    const mp4 = video.querySelector('source[type="video/mp4"][src]');
    if (mp4 && mp4.src) return mp4.src;
    return getQuestionnaireVideoUrl(video);
  }

  function getQuestionnaireVideoUrl(video) {
    if (!video) return '';
    const source = video.querySelector('source[src]');
    if (source && source.src) return source.src;
    return video.currentSrc || video.src || '';
  }

  function getVideoContainer(video) {
    if (!video) return null;
    return video.closest('.questionnaire-video-circular-container') ||
      video.closest('.questionnaire-video-wrapper');
  }

  function setVideoBuffering(video, isBuffering) {
    const container = getVideoContainer(video);
    if (container) container.classList.toggle('is-buffering', !!isBuffering);
  }

  function injectVideoLinkPreload(url) {
    if (!url || prefetchedVideoUrls.has(url)) return;
    prefetchedVideoUrls.add(url);
    if (document.querySelector('link[data-qv2-video-preload="' + CSS.escape(url) + '"]')) return;
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'video';
    link.href = url;
    link.crossOrigin = 'anonymous';
    link.setAttribute('data-qv2-video-preload', url);
    document.head.appendChild(link);
  }

  function bindVideoErrorRecovery(video) {
    if (!video || video.dataset.qv2VideoResilience === '1') return;
    video.dataset.qv2VideoResilience = '1';
    video.addEventListener('error', function () {
      const attempt = parseInt(video.dataset.qv2VideoAttempt || '0', 10);
      if (attempt >= VIDEO_RETRY_DELAYS_MS.length) return;
      const delay = VIDEO_RETRY_DELAYS_MS[attempt];
      video.dataset.qv2VideoAttempt = String(attempt + 1);
      setTimeout(function () {
        if (!video.isConnected) return;
        video.preload = 'auto';
        video.load();
      }, delay);
    });
    video.addEventListener('loadeddata', function () {
      if (video.readyState >= 2) {
        video.dataset.qv2VideoAttempt = '0';
        setVideoBuffering(video, false);
      }
    });
  }

  function preloadQuestionnaireVideo(video, mode) {
    if (!video) return;
    const preloadMode = mode === 'metadata' ? 'metadata' : 'auto';
    bindVideoErrorRecovery(video);
    video.preload = preloadMode;
    const minReadyState = preloadMode === 'auto' ? 3 : 1;
    if (video.readyState < minReadyState) {
      video.load();
    }
    if (preloadMode === 'auto') {
      const url = getQuestionnaireVideoPreloadUrl(video);
      if (url) injectVideoLinkPreload(url);
    }
  }

  function prefetchForwardStepVideos(fromStep, aheadCount) {
    const total = getTotalSteps();
    for (let i = 0; i <= aheadCount; i++) {
      const stepNumber = fromStep + i;
      if (stepNumber >= 1 && stepNumber <= total) {
        const mode = i <= 1 ? 'auto' : 'metadata';
        preloadQuestionnaireVideo(getStepVideo(stepNumber), mode);
      }
    }
  }

  function enqueueVideoWarmup(stepNumbers) {
    stepNumbers.forEach(function (stepNumber) {
      if (videoWarmupQueue.indexOf(stepNumber) === -1) {
        videoWarmupQueue.push(stepNumber);
      }
    });
    drainVideoWarmupQueue();
  }

  function drainVideoWarmupQueue() {
    if (videoWarmupActive || videoWarmupQueue.length === 0) return;

    const stepNumber = videoWarmupQueue.shift();
    const video = getStepVideo(stepNumber);
    if (!video) {
      drainVideoWarmupQueue();
      return;
    }

    videoWarmupActive = true;
    preloadQuestionnaireVideo(video, 'auto');

    function done() {
      videoWarmupActive = false;
      drainVideoWarmupQueue();
    }

    if (video.readyState >= 3) {
      setTimeout(done, 0);
      return;
    }

    video.addEventListener('canplay', done, { once: true });
    video.addEventListener('error', done, { once: true });
    setTimeout(done, VIDEO_WARMUP_QUEUE_TIMEOUT_MS);
  }

  function pauseOtherQuestionnaireVideos(activeVideo) {
    document.querySelectorAll('.questionnaire-video').forEach(function (video) {
      if (video === activeVideo) return;
      video.pause();
      video.muted = true;
      setVideoBuffering(video, false);
    });
  }

  function shouldPlayStepVideoWithSound(stepNumber) {
    return stepNumber > 1 || window.__QV2_SOUND_ENABLED === true;
  }

  function playStepVideo(video, withSound) {
    if (!video || typeof video.play !== 'function') return Promise.resolve();
    if (!withSound) {
      video.muted = true;
      return video.play().catch(function () {});
    }

    video.muted = false;
    return video.play().catch(function () {
      video.muted = true;
      return video.play().catch(function () {});
    });
  }

  function safePlayVideo(video) {
    if (!video || typeof video.play !== 'function') return Promise.resolve();
    return video.play().catch(function () {});
  }

  function prepareStepVideoPlayback(video, stepNumber) {
    if (!video) return;
    preloadQuestionnaireVideo(video, 'auto');
    const useSound = shouldPlayStepVideoWithSound(stepNumber);
    const minReadyState = 1;

    function startPlayback() {
      pauseOtherQuestionnaireVideos(video);
      setVideoBuffering(video, false);
      playStepVideo(video, useSound);
    }

    if (video.readyState >= minReadyState) {
      startPlayback();
      return;
    }

    setVideoBuffering(video, true);
    let started = false;
    function tryStartOnce() {
      if (started) return;
      started = true;
      startPlayback();
    }

    video.addEventListener('canplay', tryStartOnce, { once: true });
    video.addEventListener('loadeddata', tryStartOnce, { once: true });
    video.addEventListener('loadedmetadata', tryStartOnce, { once: true });
    setTimeout(function () {
      if (!started) tryStartOnce();
    }, 40);
  }

  function warmUpcomingQuestionnaireVideos() {
    const steps = [];
    const warmupTo = getVideoWarmupToStep();
    for (let stepNumber = VIDEO_WARMUP_FROM_STEP; stepNumber <= warmupTo; stepNumber++) {
      const video = getStepVideo(stepNumber);
      if (video && video.readyState >= 3) continue;
      steps.push(stepNumber);
    }
    enqueueVideoWarmup(steps);
  }

  function markStep1VideoReadyAndWarmup() {
    if (step1VideoReadyForWarmup) return;
    step1VideoReadyForWarmup = true;
    warmUpcomingQuestionnaireVideos();
    prewarmLazySectionMedia();
    document.dispatchEvent(new CustomEvent('qv2:step1VideoReady'));
  }

  function prefetchUpcomingFromStep(stepNumber) {
    if (!stepNumber || stepNumber < 1) return;
    prefetchForwardStepVideos(stepNumber + 1, getVideoLookahead());
  }

  function getStep1Video() {
    const inlineStep = document.querySelector(
      '[data-qv2-step="1"] .questionnaire-video, .questionnaire-step[data-step="1"] .questionnaire-video'
    );
    return inlineStep || getStepVideo(1);
  }

  function prioritizeStep1Video() {
    const video = getStep1Video();
    if (!video) return;
    preloadQuestionnaireVideo(video, 'auto');
  }

  function prioritizeStep2Video() {
    const video = getStepVideo(2);
    if (!video) return;
    preloadQuestionnaireVideo(video, 'auto');
  }

  function scheduleBackgroundVideoWarmup() {
    const step1Video = getStep1Video();

    function startWarmup() {
      markStep1VideoReadyAndWarmup();
    }

    if (!step1Video) {
      setTimeout(startWarmup, 2000);
      return;
    }

    if (step1Video.readyState >= 3) {
      startWarmup();
      return;
    }

    step1Video.addEventListener('canplay', startWarmup, { once: true });
    step1Video.addEventListener('loadeddata', function () {
      if (step1Video.readyState >= 3) startWarmup();
    }, { once: true });
    setTimeout(startWarmup, 10000);
  }

  function prefetchFromNextButton(nextStep) {
    if (!nextStep || isNaN(nextStep)) return;
    preloadQuestionnaireVideo(getStepVideo(nextStep), 'auto');
    const cacheKey = String(currentStep) + '->' + String(nextStep);
    if (prefetchFromNextButton._last === cacheKey) return;
    prefetchFromNextButton._last = cacheKey;
    prefetchUpcomingFromStep(nextStep - 1);
  }

  function bindVideoPrefetchOnNavigation() {
    if (document.body.dataset.qv2VideoPrefetchBound) return;
    document.body.dataset.qv2VideoPrefetchBound = '1';

    function handleNextPrefetch(e) {
      const nextBtn = e.target.closest('[data-next-step]');
      if (!nextBtn) return;
      const nextStep = parseInt(nextBtn.getAttribute('data-next-step'), 10);
      prefetchFromNextButton(nextStep);
    }

    document.addEventListener('mousedown', handleNextPrefetch, true);
    document.addEventListener('touchstart', handleNextPrefetch, { capture: true, passive: true });
    document.addEventListener('mouseover', function (e) {
      if (e.target.closest('[data-next-step]')) {
        handleNextPrefetch(e);
      }
    }, true);
  }

  function getSessionCacheVersion() {
    const cfgVersion = (config && config.version) || '2.0.0';
    const script = document.querySelector('script[src*="questionnaire-v2-core.js"]');
    let assetVersion = '';
    if (script && script.src) {
      const match = script.src.match(/[?&]v=([^&]+)/);
      if (match) assetVersion = match[1];
    }
    return cfgVersion + (assetVersion ? '@' + assetVersion : '');
  }

  function getSectionCacheStorageKey(sectionKey) {
    return 'qv2:sess:' + getSessionCacheVersion() + ':section:' + sectionKey;
  }

  function getMediaCacheStorageKey() {
    return 'qv2:sess:' + getSessionCacheVersion() + ':media';
  }

  function ensureSessionCacheVersion() {
    try {
      const current = getSessionCacheVersion();
      const stored = sessionStorage.getItem('qv2:sess:version');
      if (stored === current) return;
      Object.keys(sessionStorage).forEach(function (key) {
        if (key.indexOf('qv2:sess:') === 0) sessionStorage.removeItem(key);
      });
      sessionStorage.setItem('qv2:sess:version', current);
    } catch (e) {}
  }

  function readSectionCache(sectionKey) {
    try {
      const raw = sessionStorage.getItem(getSectionCacheStorageKey(sectionKey));
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed && parsed.html ? parsed : null;
    } catch (e) {
      return null;
    }
  }

  function writeSectionCache(sectionKey, html) {
    if (!html) return;
    try {
      sessionStorage.setItem(getSectionCacheStorageKey(sectionKey), JSON.stringify({
        html: html,
        savedAt: Date.now()
      }));
    } catch (e) {
      console.warn('[QV2] Cache session section indisponible:', sectionKey, e);
    }
  }

  function persistMediaCache() {
    const el = document.getElementById('qv2-questionnaire-media-data') ||
      document.getElementById('questionnaire-media-data');
    if (!el || !el.textContent) return;
    try {
      sessionStorage.setItem(getMediaCacheStorageKey(), el.textContent);
    } catch (e) {}
  }

  function restoreMediaCache() {
    try {
      const raw = sessionStorage.getItem(getMediaCacheStorageKey());
      if (!raw) return false;
      let target = document.getElementById('qv2-questionnaire-media-data');
      if (!target) {
        target = document.createElement('script');
        target.type = 'application/json';
        target.id = 'qv2-questionnaire-media-data';
        document.body.appendChild(target);
      }
      if ((target.textContent || '').length >= raw.length) return true;
      target.textContent = raw;
      window.__QV2_QUESTIONNAIRE_MEDIA__ = JSON.parse(raw);
      document.dispatchEvent(new CustomEvent('qv2:mediaDataUpdated'));
      return true;
    } catch (e) {
      return false;
    }
  }

  function mountSectionHtml(sectionKey, html) {
    const host = getHost();
    if (!host || !html) return false;

    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    adoptSectionMediaData(tmp);
    persistMediaCache();
    if (sectionKey === 'qv2_step_09_13' || sectionKey === 'qv2_step_14_15' || sectionKey === 'qv2_step_02_04' || sectionKey === 'qv2_step_05_08' || sectionKey === 'qv2_step_16' || sectionKey === 'qv2_step_17_18') {
      adoptSectionSharedUi(tmp);
    }
    tmp.querySelectorAll('.questionnaire-step[data-step]').forEach(function (stepEl) {
      const n = parseInt(stepEl.getAttribute('data-step'), 10);
      const existingInHost = host.querySelector('.questionnaire-step[data-step="' + n + '"]');

      if (existingInHost) {
        mountStepElement(stepEl, host);
        return;
      }

      const existingAnywhere = document.querySelector('.questionnaire-step[data-step="' + n + '"]');
      if (existingAnywhere && existingAnywhere !== stepEl) {
        if (n === 2 && getStepUniversCount(stepEl) > getStepUniversCount(existingAnywhere)) {
          mountStepElement(stepEl, host);
          return;
        }
        host.appendChild(existingAnywhere);
        loadedStepNumbers.add(n);
        return;
      }

      mountStepElement(stepEl, host);
    });

    return true;
  }

  function snapshotInlineSectionToCache(sectionKey) {
    const spec = INLINE_SECTIONS[sectionKey];
    if (!spec) return;
    const container = document.querySelector('[data-qv2-section="' + spec.marker + '"]');
    if (!container) return;
    writeSectionCache(sectionKey, container.innerHTML);
    persistMediaCache();
  }

  function revalidateSectionInBackground(sectionKey, url) {
    function runRevalidate() {
      fetchWithRetry(url, 2).then(function (response) {
        return response.json();
      }).then(function (data) {
        const html = data[sectionKey];
        if (!html) return;
        writeSectionCache(sectionKey, html);
        const tmp = document.createElement('div');
        tmp.innerHTML = html;
        adoptSectionMediaData(tmp);
        persistMediaCache();
      }).catch(function () {});
    }

    const activeVideo = getStepVideo(currentStep);
    if (activeVideo && activeVideo.readyState < 3) {
      activeVideo.addEventListener('canplay', runRevalidate, { once: true });
      setTimeout(runRevalidate, 12000);
      return;
    }

    runRevalidate();
  }

  function readConfig() {
    const el = document.querySelector(CONFIG_SEL);
    if (!el) return null;
    try {
      const parsed = JSON.parse(el.textContent);
      const titlesEl = document.getElementById('qv2-step-titles-data');
      if (titlesEl) {
        try {
          const titles = JSON.parse(titlesEl.textContent);
          parsed.stepTitles = Object.assign({}, parsed.stepTitles || {}, titles);
        } catch (titleErr) {
          console.warn('[QV2] Titres d\'étapes invalides', titleErr);
        }
      }
      return parsed;
    } catch (e) {
      console.error('[QV2] Config JSON invalide', e);
      return null;
    }
  }

  function getHost() {
    return document.querySelector(HOST_SEL);
  }

  function setLoading(isLoading) {
    const el = document.querySelector(LOADING_SEL);
    if (el) el.hidden = !isLoading;
  }

  function getVisualStep(realStep) {
    if (realStep <= 0) return 1;
    if (realStep < 4) return 1;
    if (realStep === 4) return 2;
    if (realStep < 8) return 2;
    if (realStep === 8) return 3;
    if (realStep < 12) return 3;
    if (realStep === 12) return 4;
    if (realStep < 18) return 4;
    if (realStep === 18) return 5;
    return 5;
  }

  function getRealStepFromVisual(visualStep) {
    if (visualStep <= 1) return 1;
    if (visualStep <= 2) return 4;
    if (visualStep <= 3) return 8;
    if (visualStep <= 4) return 12;
    return 18;
  }

  function getStepTitle(stepNumber) {
    if (!config || !config.stepTitles) return 'ÉTAPE ' + stepNumber;
    return config.stepTitles[String(stepNumber)] || ('ÉTAPE ' + stepNumber);
  }

  function updateProgressBarFill(bar, stepNumber) {
    if (!bar) return;

    const stepsPerSegment = 4;
    const segmentIndex = Math.floor((stepNumber - 1) / stepsPerSegment);
    const stepInSegment = ((stepNumber - 1) % stepsPerSegment) + 1;
    const segmentProgress = stepInSegment / stepsPerSegment;
    const segmentSize = 25;
    const baseProgress = segmentIndex * segmentSize;
    const progressPercentage = Math.min(100, Math.max(0, baseProgress + (segmentProgress * segmentSize)));

    const container = bar.parentElement;
    if (!container) return;

    const containerWidth = container.offsetWidth;
    const trackInset = 0.08;
    const trackWidth = containerWidth * (1 - (2 * trackInset));
    const fillWidth = (trackWidth * progressPercentage) / 100;

    bar.style.left = (containerWidth * trackInset) + 'px';
    bar.style.right = 'auto';
    bar.style.width = fillWidth + 'px';
  }

  function updateProgressStepCursors() {
    const shell = document.querySelector('.qv2-shell');
    if (!shell) return;

    const currentVisual = getVisualStep(currentStep);
    shell.querySelectorAll('.progress-step').forEach(function (el, index) {
      const flowerNumber = index + 1;
      const canNavigateBack = flowerNumber < currentVisual ||
        (flowerNumber === currentVisual &&
          getRealStepFromVisual(flowerNumber) < currentStep);
      el.style.cursor = canNavigateBack ? 'pointer' : 'default';
      el.setAttribute('aria-disabled', canNavigateBack ? 'false' : 'true');
    });
  }

  function updateProgressUI(stepNumber) {
    const visual = getVisualStep(stepNumber);
    const counter = document.getElementById('qv2-progress-counter');
    const title = document.getElementById('qv2-progress-title');
    const bar = document.getElementById('qv2-progress-bar-fill');

    if (counter) {
      counter.textContent = 'Étape ' + stepNumber + ' sur ' + (config?.totalSteps || 18);
    }
    if (title) {
      title.textContent = getStepTitle(stepNumber);
    }
    if (bar) {
      updateProgressBarFill(bar, stepNumber);
    }

    document.querySelectorAll('.qv2-shell .progress-step').forEach(function (el, index) {
      el.classList.toggle('active', index + 1 <= visual);
    });

    updateProgressStepCursors();
  }

  function hideAllSteps() {
    getHost()?.querySelectorAll('.questionnaire-step[data-step]').forEach(function (step) {
      step.style.display = 'none';
      const video = step.querySelector('.questionnaire-video');
      if (video) {
        video.pause();
        video.muted = true;
      }
      const videoContainer = step.querySelector('.questionnaire-video-circular-container');
      if (videoContainer) {
        videoContainer.classList.remove('is-expanded');
        videoContainer.classList.remove('is-buffering');
      }
    });
  }

  function showStepElement(stepNumber) {
    const host = getHost();
    if (!host) return false;
    const step = host.querySelector('.questionnaire-step[data-step="' + stepNumber + '"]');
    if (!step) return false;

    hideAllSteps();
    step.style.display = 'block';
    currentStep = stepNumber;
    updateProgressUI(stepNumber);

    const video = step.querySelector('.questionnaire-video');
    if (video) {
      prepareStepVideoPlayback(video, stepNumber);
    }

    initVideoControls(step);
    ensureSharedUiInBody();
    notifyStepShown(stepNumber);
    scheduleStepImageHydration(step, stepNumber);
    prefetchUpcomingFromStep(stepNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    return true;
  }

  function notifyStepShown(stepNumber) {
    document.dispatchEvent(new CustomEvent('qv2:stepShown', {
      detail: { step: stepNumber }
    }));
    if (window.QV2_WEDDING_DATE && typeof window.QV2_WEDDING_DATE.capture === 'function') {
      window.QV2_WEDDING_DATE.capture();
    }
    if (stepNumber === 17 && window.QV2_WEDDING_DATE && typeof window.QV2_WEDDING_DATE.syncStep17 === 'function') {
      window.QV2_WEDDING_DATE.syncStep17();
    }
    if (window.QV2_STEPS_02_04 && typeof window.QV2_STEPS_02_04.onStepShown === 'function') {
      window.QV2_STEPS_02_04.onStepShown(stepNumber);
    }
    if (window.QV2_STEPS_05_08 && typeof window.QV2_STEPS_05_08.onStepShown === 'function') {
      window.QV2_STEPS_05_08.onStepShown(stepNumber);
    }
    if (window.QV2_STEPS_09_13 && typeof window.QV2_STEPS_09_13.onStepShown === 'function') {
      window.QV2_STEPS_09_13.onStepShown(stepNumber);
    }
    if (window.QV2_STEPS_14_15 && typeof window.QV2_STEPS_14_15.onStepShown === 'function') {
      window.QV2_STEPS_14_15.onStepShown(stepNumber);
    }
    if (window.QV2_STEPS_16 && typeof window.QV2_STEPS_16.onStepShown === 'function') {
      window.QV2_STEPS_16.onStepShown(stepNumber);
    }
    if (window.QV2_STEPS_17_18 && typeof window.QV2_STEPS_17_18.onStepShown === 'function') {
      window.QV2_STEPS_17_18.onStepShown(stepNumber);
    }
    if (stepNumber === 17 && window.QV2_STEPS_17_18 && typeof window.QV2_STEPS_17_18.syncWeddingDate === 'function') {
      window.QV2_STEPS_17_18.syncWeddingDate();
    }
  }

  function extractStepFromSectionHtml(html, stepNumber) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const step = doc.querySelector('.questionnaire-step[data-step="' + stepNumber + '"]');
    if (step) return step.outerHTML;

    const shopifySection = doc.querySelector('.shopify-section');
    if (shopifySection) return shopifySection.innerHTML;

    return html;
  }

  const INLINE_SECTIONS = {
    qv2_step_02_04: { marker: '02-04', steps: [2, 3, 4] },
    qv2_step_05_08: { marker: '05-08', steps: [5, 6, 7, 8] },
    qv2_step_09_13: { marker: '09-13', steps: [9, 10, 11, 12, 13] },
    qv2_step_14_15: { marker: '14-15', steps: [14, 15] },
    qv2_step_16: { marker: '16', steps: [16] },
    qv2_step_17_18: { marker: '17-18', steps: [17, 18] }
  };

  function adoptSectionMediaData(rootEl) {
    if (!rootEl) return;
    const mediaEl = rootEl.querySelector('#qv2-questionnaire-media-data') ||
      rootEl.querySelector('#questionnaire-media-data');
    if (!mediaEl || !mediaEl.textContent) return;

    let target = document.getElementById('qv2-questionnaire-media-data');
    if (!target) {
      document.body.appendChild(mediaEl);
      target = mediaEl;
    } else if (target !== mediaEl && mediaEl.textContent.length > (target.textContent || '').length) {
      target.textContent = mediaEl.textContent;
    }

    try {
      window.__QV2_QUESTIONNAIRE_MEDIA__ = JSON.parse(target.textContent);
      document.dispatchEvent(new CustomEvent('qv2:mediaDataUpdated'));
      persistMediaCache();
    } catch (e) {
      console.warn('[QV2] questionnaire-media-data invalide:', e);
    }
  }

  function adoptSectionConfigData(rootEl) {
    if (!rootEl) return;
    const configEl = rootEl.querySelector('#qv2-questionnaire-config-data');
    if (!configEl) return;

    let target = document.getElementById('qv2-questionnaire-config-data');
    if (!target) {
      document.body.appendChild(configEl);
      target = configEl;
    }

    if (target && target.textContent && window.QV2_STEPS_17_18 && typeof window.QV2_STEPS_17_18.init === 'function') {
      window.QV2_STEPS_17_18.init();
    }
  }

  function adoptElementToBody(el) {
    if (!el) return;
    if (el.parentElement === document.body) return;
    document.body.appendChild(el);
  }

  const SHARED_UI_IDS = [
    'questionnaire-image-lightbox',
    'univers-gallery-modal',
    'decoration-gallery-modal',
    'step9-sub-gallery-modal',
    'step10-arche-gallery-modal',
    'time-help-modal'
  ];

  function ensureSharedUiInBody() {
    SHARED_UI_IDS.forEach(function (id) {
      const inBody = document.body.querySelector('#' + id);
      if (inBody) {
        document.querySelectorAll('#' + id).forEach(function (node) {
          if (node !== inBody) node.remove();
        });
        return;
      }
      const el = document.getElementById(id);
      if (el) adoptElementToBody(el);
    });
    document.querySelectorAll('.material-help-modal').forEach(function (modal) {
      adoptElementToBody(modal);
    });
  }

  function getUncroppedImageUrl(rawUrl) {
    if (!rawUrl || typeof rawUrl !== 'string') return rawUrl;
    let url = rawUrl.trim().replace(/&amp;/g, '&');
    url = url.replace(/_\d+x\d+_crop_center(?=\.)/i, '');
    url = url.replace(/_crop_center(?=\.)/i, '');
    url = url.replace(/_\d+x\d+(?=\.)/i, '');
    url = url.replace(/_\d+x(?=\.)/i, '');
    url = url.replace(/_x\d+(?=\.)/i, '');
    url = url.replace(/([?&])width=\d+(&|$)/i, '$1');
    url = url.replace(/([?&])height=\d+(&|$)/i, '$1');
    url = url.replace(/([?&])crop=[^&]*(&|$)/i, '$1');
    url = url.replace(/\?&/, '?').replace(/&&/g, '&').replace(/[?&]$/, '');
    return url;
  }

  function openImageLightbox(imageSrc) {
    if (!imageSrc) return false;
    ensureSharedUiInBody();
    const lightbox = document.getElementById('questionnaire-image-lightbox');
    const lightboxImage = document.getElementById('lightbox-image');
    if (!lightbox || !lightboxImage) return false;
    lightboxImage.src = getUncroppedImageUrl(imageSrc);
    lightbox.classList.add('active');
    document.body.style.overflow = 'hidden';
    return true;
  }

  function closeImageLightbox() {
    const lightbox = document.getElementById('questionnaire-image-lightbox');
    if (!lightbox) return;
    lightbox.classList.remove('active');
    document.body.style.overflow = '';
  }

  function initGlobalLightboxBindings() {
    if (document.body.dataset.qv2GlobalLightboxBound) return;
    document.body.dataset.qv2GlobalLightboxBound = '1';
    ensureSharedUiInBody();
    const lightboxClose = document.getElementById('lightbox-close');
    const lightbox = document.getElementById('questionnaire-image-lightbox');
    const lightboxOverlay = lightbox ? lightbox.querySelector('.lightbox-overlay') : null;
    if (lightboxClose) {
      lightboxClose.addEventListener('click', closeImageLightbox);
    }
    if (lightboxOverlay) {
      lightboxOverlay.addEventListener('click', closeImageLightbox);
    }
    document.addEventListener('keydown', function (e) {
      const activeLightbox = document.getElementById('questionnaire-image-lightbox');
      if (e.key === 'Escape' && activeLightbox && activeLightbox.classList.contains('active')) {
        closeImageLightbox();
      }
    });
  }

  function resolveZoomImageSrc(zoomBtn) {
    if (!zoomBtn) return '';
    const direct = zoomBtn.getAttribute('data-lightbox-src');
    if (direct) return direct;
    const card = zoomBtn.closest(
      '.univers-item, .decoration-item, .material-item, .table-honneur-option, .container-item, .gift-bag-item, .flower-variety-item, .element-item, .accessory-card'
    );
    if (!card) return '';
    const img = card.querySelector('img[data-lightbox-src], img[data-fallback-src]');
    if (!img) return '';
    return img.getAttribute('data-lightbox-src') || img.getAttribute('data-fallback-src') || img.getAttribute('src') || '';
  }

  function initGlobalImageZoomCapture() {
    if (document.body.dataset.qv2GlobalZoomCapture) return;
    document.body.dataset.qv2GlobalZoomCapture = '1';
    document.addEventListener('click', function (e) {
      if (e.target.closest('.material-help-btn')) return;
      const zoomBtn = e.target.closest('.image-zoom-btn');
      if (!zoomBtn) return;
      if (zoomBtn.closest('.univers-item, .decoration-item, .table-honneur-suboption, #arche-fresh, #arche-artificial, #step11-fresh-option, #step11-artificial-option, .container-item, .gift-bag-item')) {
        return;
      }
      const src = resolveZoomImageSrc(zoomBtn);
      if (!src) return;
      e.preventDefault();
      e.stopPropagation();
      openImageLightbox(src);
    }, true);
  }

  function adoptSectionSharedUi(rootEl) {
    if (!rootEl) return;
    SHARED_UI_IDS.forEach(function (id) {
      const el = rootEl.querySelector('#' + id);
      if (el) adoptElementToBody(el);
    });
    rootEl.querySelectorAll('.material-help-modal').forEach(function (modal) {
      if (modal.id) adoptElementToBody(modal);
    });
  }

  function adoptInlineSectionSteps(marker, stepNumbers, sectionKey) {
    const host = getHost();
    if (!host) return false;

    const container = document.querySelector('[data-qv2-section="' + marker + '"]');
    if (!container) return false;

    adoptSectionMediaData(container);
    adoptSectionSharedUi(container);
    if (marker === '17-18') {
      adoptSectionConfigData(container);
      adoptSectionSharedUi(container);
    }

    stepNumbers.forEach(function (n) {
      if (loadedStepNumbers.has(n)) return;
      const step = container.querySelector('.questionnaire-step[data-step="' + n + '"]');
      if (step && !host.contains(step)) {
        host.appendChild(step);
        loadedStepNumbers.add(n);
      }
    });

    const allLoaded = stepNumbers.every(function (n) {
      return loadedStepNumbers.has(n) || host.querySelector('.questionnaire-step[data-step="' + n + '"]');
    });

    if (allLoaded) {
      stepNumbers.forEach(function (n) { loadedStepNumbers.add(n); });
      loadedSectionKeys.add(sectionKey);
      snapshotInlineSectionToCache(sectionKey);
      return true;
    }
    return false;
  }

  function adoptInlineStepsForSection(sectionKey) {
    const spec = INLINE_SECTIONS[sectionKey];
    if (!spec) return false;
    return adoptInlineSectionSteps(spec.marker, spec.steps, sectionKey);
  }

  function getSectionKeyForStep(stepNumber) {
    if (!config?.stepSections) return null;
    return config.stepSections[String(stepNumber)] || null;
  }

  function mountStepHtml(stepNumber, html) {
    const host = getHost();
    if (!host) return;

    const existing = host.querySelector('.questionnaire-step[data-step="' + stepNumber + '"]');
    if (existing) existing.remove();

    const wrapper = document.createElement('div');
    wrapper.innerHTML = extractStepFromSectionHtml(html, stepNumber);
    while (wrapper.firstChild) {
      host.appendChild(wrapper.firstChild);
    }
    loadedStepNumbers.add(stepNumber);
  }

  function renderPlaceholderStep(stepNumber) {
    return (
      '<div class="questionnaire-step qv2-step qv2-step--placeholder" data-step="' + stepNumber + '" data-qv2-step="' + stepNumber + '">' +
        '<div class="questionnaire-content qv2-placeholder">' +
          '<h2 class="questionnaire-main-title">Étape ' + stepNumber + '</h2>' +
          '<p>Cette étape sera disponible prochainement dans la version optimisée du devis.</p>' +
          '<div class="qv2-placeholder__actions">' +
            (stepNumber > 1 ? '<button type="button" class="questionnaire-btn questionnaire-btn-secondary qv2-btn-prev" data-prev-step="' + (stepNumber - 1) + '">Retour</button>' : '') +
            (stepNumber < (config?.totalSteps || 18) ? '<button type="button" class="questionnaire-btn qv2-btn-next" data-next-step="' + (stepNumber + 1) + '">Étape suivante (aperçu)</button>' : '') +
          '</div>' +
        '</div>' +
      '</div>'
    );
  }

  function getStepUniversCount(stepEl) {
    if (!stepEl) return 0;
    return stepEl.querySelectorAll('.questionnaire-univers-grid .univers-item').length;
  }

  function mountStepElement(stepEl, host) {
    if (!stepEl || !host) return;
    const stepNumber = parseInt(stepEl.getAttribute('data-step'), 10);
    const existing = host.querySelector('.questionnaire-step[data-step="' + stepNumber + '"]');
    if (existing) {
      if (stepNumber === 2) {
        if (getStepUniversCount(stepEl) <= getStepUniversCount(existing)) {
          loadedStepNumbers.add(stepNumber);
          return;
        }
        existing.replaceWith(stepEl);
      } else {
        loadedStepNumbers.add(stepNumber);
        return;
      }
    } else {
      host.appendChild(stepEl);
    }
    loadedStepNumbers.add(stepNumber);
  }

  async function reloadStepSection(sectionKey, stepNumber) {
    const host = getHost();
    if (!host) return false;

    loadedSectionKeys.delete(sectionKey);
    loadedStepNumbers.delete(stepNumber);

    const existing = host.querySelector('.questionnaire-step[data-step="' + stepNumber + '"]');
    if (existing) existing.remove();

    return fetchStepSection(sectionKey, true);
  }

  async function fetchStepSection(sectionKey, forceReload) {
    if (!forceReload && loadedSectionKeys.has(sectionKey)) return true;

    const pageUrl = window.QV2_PAGE_URL || window.location.pathname;
    const url = pageUrl + (pageUrl.includes('?') ? '&' : '?') + 'sections=' + encodeURIComponent(sectionKey);

    if (!forceReload) {
      const cached = readSectionCache(sectionKey);
      if (cached && cached.html && getHost() && mountSectionHtml(sectionKey, cached.html)) {
        loadedSectionKeys.add(sectionKey);
        revalidateSectionInBackground(sectionKey, url);
        return true;
      }
    }

    try {
      const response = await fetchWithRetry(url);
      const data = await response.json();
      const html = data[sectionKey];
      if (!html) throw new Error('Section vide: ' + sectionKey);

      writeSectionCache(sectionKey, html);
      if (!getHost() || !mountSectionHtml(sectionKey, html)) return false;

      loadedSectionKeys.add(sectionKey);
      return true;
    } catch (err) {
      const cached = readSectionCache(sectionKey);
      if (cached && cached.html && getHost() && mountSectionHtml(sectionKey, cached.html)) {
        loadedSectionKeys.add(sectionKey);
        console.warn('[QV2] Section restaurée depuis le cache session:', sectionKey);
        return true;
      }
      throw err;
    }
  }

  async function ensureStepLoaded(stepNumber) {
    if (loadedStepNumbers.has(stepNumber)) return true;

    const sectionKey = getSectionKeyForStep(stepNumber);
    if (!sectionKey) {
      mountStepHtml(stepNumber, renderPlaceholderStep(stepNumber));
      return true;
    }

    if (sectionKey === 'qv2_step_01' && stepNumber === 1) {
      adoptInlineStep1();
      if (!loadedStepNumbers.has(1)) {
        try {
          await fetchStepSection(sectionKey);
        } catch (e) {
          console.warn('[QV2] Étape 1 non disponible:', e);
        }
        adoptInlineStep1();
      }
      return true;
    }

    try {
      if (!adoptInlineStepsForSection(sectionKey)) {
        await fetchStepSection(sectionKey);
      }
      if (!loadedStepNumbers.has(stepNumber)) {
        adoptInlineStepsForSection(sectionKey);
      }
      if (!loadedStepNumbers.has(stepNumber)) {
        mountStepHtml(stepNumber, renderPlaceholderStep(stepNumber));
      }
      return true;
    } catch (e) {
      console.warn('[QV2] Section non disponible:', sectionKey, e);
      mountStepHtml(stepNumber, renderPlaceholderStep(stepNumber));
      return true;
    }
  }

  async function showStep(stepNumber) {
    const total = config?.totalSteps || 18;
    if (stepNumber < 1 || stepNumber > total) return;

    if (stepNumber > 1) {
      window.__QV2_SOUND_ENABLED = true;
    }

    captureWeddingDateSnapshot();

    setLoading(true);
    try {
      await ensureStepLoaded(stepNumber);
      if (!showStepElement(stepNumber)) {
        mountStepHtml(stepNumber, renderPlaceholderStep(stepNumber));
        showStepElement(stepNumber);
      }
      if (stepNumber > 1) {
        prefetchUpcomingFromStep(stepNumber);
      }
      bindStepActions();
    } finally {
      setLoading(false);
    }
  }

  function getWeddingDateStoreEl() {
    return document.getElementById('qv2-wedding-date-store');
  }

  function readWeddingDateStore() {
    const el = getWeddingDateStoreEl();
    return el ? (el.value || '').trim() : '';
  }

  function syncWeddingDateStore(value) {
    const el = getWeddingDateStoreEl();
    if (el) el.value = value || '';
    if (value) {
      document.body.setAttribute('data-qv2-wedding-date', value);
    } else {
      document.body.removeAttribute('data-qv2-wedding-date');
    }
  }

  function forceFlushWeddingDateInput() {
    const inputs = findWeddingDateInputs();
    for (let i = 0; i < inputs.length; i++) {
      const input = inputs[i];
      if (input && typeof input.blur === 'function') {
        input.blur();
      }
      const value = (input && input.value ? input.value : '').trim();
      if (value) {
        setPersistedWeddingDate(value);
        return value;
      }
    }
    return '';
  }

  function formatDateToISO(date) {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return year + '-' + month + '-' + day;
  }

  function formatISOToFrench(isoDate) {
    if (!isoDate || !/^\d{4}-\d{2}-\d{2}$/.test(isoDate)) return isoDate || '';
    const parts = isoDate.split('-');
    return parts[2] + '/' + parts[1] + '/' + parts[0];
  }

  /** Aligné sur l'original : première date autorisée = aujourd'hui + 1 mois civil. */
  function getMinimumWeddingDate() {
    const today = new Date();
    const minDate = new Date(today.getFullYear(), today.getMonth() + 1, today.getDate());
    return formatDateToISO(minDate);
  }

  function isWeddingDateWithinAllowedRange(value, minDate) {
    if (!value || !minDate) return false;
    return value >= minDate;
  }

  function applyWeddingDateMinimum(input, minDate) {
    if (!input || !minDate) return;
    input.setAttribute('min', minDate);
    input.setAttribute('lang', 'fr-FR');
    input.setAttribute('data-locale', 'fr-FR');
    input.setAttribute('placeholder', 'jj/mm/aaaa');
    input.setAttribute(
      'title',
      'Format : jj/mm/aaaa. Première date disponible : ' + formatISOToFrench(minDate)
    );
  }

  function enforceWeddingDateMinimum(input, minDate) {
    if (!input || !minDate || !input.value) return true;
    if (isWeddingDateWithinAllowedRange(input.value, minDate)) return true;
    input.value = minDate;
    if (typeof input.setCustomValidity === 'function') {
      input.setCustomValidity('');
    }
    return false;
  }

  function setWeddingDateCustomValidity(input, minDate) {
    if (!input || typeof input.setCustomValidity !== 'function') return;
    const value = (input.value || '').trim();
    if (!value) {
      input.setCustomValidity('');
      return;
    }
    input.setCustomValidity(
      isWeddingDateWithinAllowedRange(value, minDate)
        ? ''
        : 'La date du mariage doit être au moins dans un mois.'
    );
  }

  function updateDatePlaceholder(input) {
    const placeholder = document.getElementById('qv2-date-placeholder') ||
      document.getElementById('date-placeholder');
    if (!placeholder || !input) return;

    const isIOSSafari = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    if (isIOSSafari) {
      if (!input.value) {
        placeholder.classList.add('show');
        placeholder.classList.remove('hide');
      } else {
        placeholder.classList.add('hide');
        placeholder.classList.remove('show');
      }
    } else {
      placeholder.classList.add('hide');
      placeholder.classList.remove('show');
    }
  }

  function findWeddingDateInputs() {
    return Array.prototype.slice.call(
      document.querySelectorAll('#qv2-wedding-date, #wedding-date, input[name="wedding-date"]')
    );
  }

  function getPersistedWeddingDate() {
    try {
      return sessionStorage.getItem(WEDDING_DATE_STORAGE_KEY) ||
        localStorage.getItem(WEDDING_DATE_STORAGE_KEY) ||
        '';
    } catch (e) {
      return '';
    }
  }

  function setPersistedWeddingDate(value) {
    weddingDateMemory = value || '';
    if (window.QV2_WEDDING_DATE && typeof window.QV2_WEDDING_DATE.set === 'function') {
      window.QV2_WEDDING_DATE.set(value);
      return;
    }
    syncWeddingDateStore(value);
    try {
      if (value) {
        sessionStorage.setItem(WEDDING_DATE_STORAGE_KEY, value);
        localStorage.setItem(WEDDING_DATE_STORAGE_KEY, value);
      } else {
        sessionStorage.removeItem(WEDDING_DATE_STORAGE_KEY);
        localStorage.removeItem(WEDDING_DATE_STORAGE_KEY);
      }
    } catch (e) {}
  }

  function readWeddingDateFromInputs() {
    const host = getHost();
    if (host) {
      const hostInput = host.querySelector(
        '[data-step="1"] #qv2-wedding-date, [data-step="1"] #wedding-date, [data-step="1"] input[name="wedding-date"]'
      );
      if (hostInput) {
        const hostValue = (hostInput.value || '').trim();
        if (hostValue) return hostValue;
      }
    }

    const inputs = findWeddingDateInputs();
    for (let i = 0; i < inputs.length; i++) {
      const value = (inputs[i].value || '').trim();
      if (value) return value;
    }
    return '';
  }

  function captureWeddingDateSnapshot() {
    if (window.QV2_WEDDING_DATE && typeof window.QV2_WEDDING_DATE.capture === 'function') {
      const fromBridge = window.QV2_WEDDING_DATE.capture();
      if (fromBridge) {
        weddingDateMemory = fromBridge;
        return fromBridge;
      }
    }

    const fromDom = forceFlushWeddingDateInput() || readWeddingDateFromInputs();
    if (fromDom) {
      setPersistedWeddingDate(fromDom);
      return fromDom;
    }

    if (weddingDateMemory) return weddingDateMemory;
    const fromStore = readWeddingDateStore();
    if (fromStore) {
      weddingDateMemory = fromStore;
      return fromStore;
    }
    const bodyDate = (document.body.getAttribute('data-qv2-wedding-date') || '').trim();
    if (bodyDate) {
      weddingDateMemory = bodyDate;
      syncWeddingDateStore(bodyDate);
      return bodyDate;
    }
    const stored = getPersistedWeddingDate();
    if (stored) {
      weddingDateMemory = stored;
      syncWeddingDateStore(stored);
    }
    return stored;
  }

  function getWeddingDate() {
    return captureWeddingDateSnapshot();
  }

  function dedupeStep1InHost(host) {
    if (!host) return;
    const steps = host.querySelectorAll('.questionnaire-step[data-step="1"]');
    if (steps.length <= 1) return;

    let keeper = null;
    let bestValue = readWeddingDateStore() || getPersistedWeddingDate() || weddingDateMemory || '';

    steps.forEach(function (step) {
      const input = step.querySelector('#qv2-wedding-date, #wedding-date, input[name="wedding-date"]');
      const value = input && input.value ? input.value.trim() : '';
      if (value) {
        bestValue = value;
        keeper = step;
      }
    });
    if (!keeper) keeper = steps[0];

    const keeperInput = keeper.querySelector('#qv2-wedding-date, #wedding-date, input[name="wedding-date"]');
    if (keeperInput && bestValue && !keeperInput.value) {
      keeperInput.value = bestValue;
    }

    steps.forEach(function (step) {
      if (step !== keeper) step.remove();
    });

    if (bestValue) setPersistedWeddingDate(bestValue);
  }

  function updateStep1ValidateButton() {
    const btn = document.getElementById('qv2-validate-step-1');
    if (!btn) return;
    const min = getMinimumWeddingDate();
    const value = readWeddingDateFromInputs() || readWeddingDateStore() || weddingDateMemory;
    btn.disabled = !isWeddingDateWithinAllowedRange(value, min);
  }

  function bindStep1ValidateCapture() {
    const btn = document.getElementById('qv2-validate-step-1');
    if (!btn || btn.dataset.qv2WeddingCaptureBound) return;
    btn.dataset.qv2WeddingCaptureBound = '1';
    btn.addEventListener('click', function () {
      forceFlushWeddingDateInput();
    }, true);
  }

  function bindWeddingDateInputs() {
    const inputs = findWeddingDateInputs();
    if (!inputs.length) return;

    const min = getMinimumWeddingDate();
    const stored = captureWeddingDateSnapshot();

    inputs.forEach(function (input) {
      applyWeddingDateMinimum(input, min);
      if (stored && !input.value) {
        input.value = stored;
      }
      if (input.value && !isWeddingDateWithinAllowedRange(input.value, min)) {
        enforceWeddingDateMinimum(input, min);
      }
      setWeddingDateCustomValidity(input, min);
      updateDatePlaceholder(input);
    });

    function persistAndNotify() {
      const value = readWeddingDateFromInputs();
      if (value) setPersistedWeddingDate(value);
      document.dispatchEvent(new CustomEvent('qv2:weddingDateChanged', {
        detail: { value: value || '' }
      }));
    }

    function handleDateChange(input) {
      const minDate = input.getAttribute('min') || getMinimumWeddingDate();
      enforceWeddingDateMinimum(input, minDate);
      setWeddingDateCustomValidity(input, minDate);
      updateDatePlaceholder(input);
      persistAndNotify();
      updateStep1ValidateButton();
    }

    inputs.forEach(function (input) {
      if (input.dataset.qv2DateBound) return;
      input.dataset.qv2DateBound = '1';

      input.addEventListener('change', function () {
        handleDateChange(input);
      });
      input.addEventListener('input', function () {
        const minDate = input.getAttribute('min') || getMinimumWeddingDate();
        setWeddingDateCustomValidity(input, minDate);
        updateDatePlaceholder(input);
        persistAndNotify();
        updateStep1ValidateButton();
      });
      input.addEventListener('blur', function () {
        handleDateChange(input);
      });
      input.addEventListener('focus', function () {
        updateDatePlaceholder(input);
      });

      const wrapper = input.closest('.date-input-wrapper');
      if (wrapper && !wrapper.dataset.qv2DatePickerBound) {
        wrapper.dataset.qv2DatePickerBound = '1';
        wrapper.addEventListener('click', function (e) {
          if (!window.matchMedia('(min-width: 769px) and (pointer: fine)').matches) return;
          if (e.target && e.target.closest && e.target.closest('label.date-calendar-icon')) return;
          input.focus();
          if (typeof input.showPicker === 'function') {
            try { input.showPicker(); } catch (err) { input.click(); }
          } else {
            input.click();
          }
        });
      }
    });

    if (stored && isWeddingDateWithinAllowedRange(stored, min)) {
      setPersistedWeddingDate(stored);
    } else if (stored && !isWeddingDateWithinAllowedRange(stored, min)) {
      const corrected = readWeddingDateFromInputs() || min;
      setPersistedWeddingDate(corrected);
    }

    persistAndNotify();
    updateStep1ValidateButton();
  }

  function initStep1Date() {
    bindWeddingDateInputs();
    bindStep1ValidateCapture();
    updateStep1ValidateButton();
  }

  function initVideoControls(stepRoot) {
    const video = stepRoot.querySelector('.questionnaire-video');
    if (!video) return;

    const container = stepRoot.querySelector('.questionnaire-video-circular-container') || stepRoot;
    const playBtn = container.querySelector('.video-play-pause');
    const muteBtn = container.querySelector('.video-mute');
    const expandBtn = container.querySelector('.video-expand');

    if (playBtn && !playBtn.dataset.qv2Bound) {
      playBtn.dataset.qv2Bound = '1';
      playBtn.addEventListener('click', function () {
        if (video.paused) {
          pauseOtherQuestionnaireVideos(video);
          window.__QV2_SOUND_ENABLED = true;
          playStepVideo(video, true);
        } else {
          video.pause();
        }
        syncPlayIcons(video, playBtn);
      });
    }

    if (muteBtn && !muteBtn.dataset.qv2Bound) {
      muteBtn.dataset.qv2Bound = '1';
      muteBtn.addEventListener('click', function () {
        video.muted = !video.muted;
        window.__QV2_SOUND_ENABLED = !video.muted;
        syncMuteIcons(video, muteBtn);
      });
    }

    if (expandBtn && !expandBtn.dataset.qv2Bound) {
      expandBtn.dataset.qv2Bound = '1';
      expandBtn.addEventListener('click', function (e) {
        e.stopPropagation();
        const isExpanded = container.classList.contains('is-expanded');
        const expandIcon = expandBtn.querySelector('.expand-icon');
        const closeIcon = expandBtn.querySelector('.close-icon');

        if (isExpanded) {
          container.classList.remove('is-expanded');
          if (expandIcon) expandIcon.style.display = '';
          if (closeIcon) closeIcon.style.display = 'none';
          expandBtn.setAttribute('aria-label', 'Agrandir');
        } else {
          container.classList.add('is-expanded');
          if (expandIcon) expandIcon.style.display = 'none';
          if (closeIcon) closeIcon.style.display = '';
          expandBtn.setAttribute('aria-label', 'Réduire');
          container.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
    }

    video.addEventListener('play', function () { syncPlayIcons(video, playBtn); });
    video.addEventListener('pause', function () { syncPlayIcons(video, playBtn); });
    syncPlayIcons(video, playBtn);
    syncMuteIcons(video, muteBtn);
  }

  function syncPlayIcons(video, btn) {
    if (!btn) return;
    const pause = btn.querySelector('.pause-icon');
    const play = btn.querySelector('.play-icon');
    if (video.paused) {
      if (pause) pause.style.display = 'none';
      if (play) play.style.display = '';
    } else {
      if (pause) pause.style.display = '';
      if (play) play.style.display = 'none';
    }
  }

  function syncMuteIcons(video, btn) {
    if (!btn) return;
    const mute = btn.querySelector('.mute-icon');
    const unmute = btn.querySelector('.unmute-icon');
    if (video.muted) {
      if (mute) mute.style.display = 'none';
      if (unmute) unmute.style.display = '';
    } else {
      if (mute) mute.style.display = '';
      if (unmute) unmute.style.display = 'none';
    }
  }

  function bindStepActions() {
    document.querySelectorAll('[data-next-step]').forEach(function (btn) {
      if (btn.dataset.qv2NavBound) return;
      btn.dataset.qv2NavBound = '1';
      btn.addEventListener('click', function () {
        forceFlushWeddingDateInput();
        window.__QV2_SOUND_ENABLED = true;
        const next = parseInt(btn.getAttribute('data-next-step'), 10);
        if (!isNaN(next)) showStep(next);
      });
    });

    document.querySelectorAll('[data-prev-step]').forEach(function (btn) {
      if (btn.dataset.qv2NavBound) return;
      btn.dataset.qv2NavBound = '1';
      btn.addEventListener('click', function () {
        forceFlushWeddingDateInput();
        window.__QV2_SOUND_ENABLED = true;
        const prev = parseInt(btn.getAttribute('data-prev-step'), 10);
        if (!isNaN(prev)) showStep(prev);
      });
    });
  }

  function bindProgressStepNavigation() {
    const shell = document.querySelector('.qv2-shell');
    if (!shell || shell.dataset.qv2ProgressNavBound) return;
    shell.dataset.qv2ProgressNavBound = '1';

    shell.querySelectorAll('.progress-step').forEach(function (stepEl) {
      if (stepEl.dataset.qv2ProgressBound) return;
      stepEl.dataset.qv2ProgressBound = '1';

      stepEl.addEventListener('click', function () {
        const visualStepNum = parseInt(this.getAttribute('data-step-number'), 10);
        if (!visualStepNum || isNaN(visualStepNum)) return;

        const currentVisual = getVisualStep(currentStep);
        if (visualStepNum > currentVisual) return;

        const realStep = getRealStepFromVisual(visualStepNum);
        const total = config?.totalSteps || 18;
        if (realStep >= 1 && realStep < currentStep && realStep <= total) {
          forceFlushWeddingDateInput();
          showStep(realStep);
        }
      });
    });

    updateProgressStepCursors();
  }

  function prewarmLazySectionMedia() {
    restoreMediaCache();
    ['02-04', '05-08', '09-13', '14-15', '16', '17-18'].forEach(function (marker) {
      const container = document.querySelector('[data-qv2-section="' + marker + '"]');
      if (!container) return;
      adoptSectionMediaData(container);
      adoptSectionSharedUi(container);
      if (marker === '17-18') {
        adoptSectionConfigData(container);
      }
    });
    adoptInlineStepsForSection('qv2_step_02_04');
    ensureSharedUiInBody();
    Object.keys(INLINE_SECTIONS).forEach(snapshotInlineSectionToCache);
  }

  function adoptInlineStep1() {
    const host = getHost();
    if (!host) return;

    const inlineStep = document.querySelector('[data-qv2-step="1"], .qv2-step-section .questionnaire-step[data-step="1"]');
    if (!inlineStep) return;

    if (!host.contains(inlineStep)) {
      host.appendChild(inlineStep);
    }

    dedupeStep1InHost(host);
    loadedStepNumbers.add(1);
    loadedSectionKeys.add('qv2_step_01');
    if (window.QV2_WEDDING_DATE && typeof window.QV2_WEDDING_DATE.capture === 'function') {
      window.QV2_WEDDING_DATE.capture();
    }
    initStep1Date();
    bindStep1ValidateCapture();
  }

  function init() {
    document.body.classList.add('has-questionnaire');
    config = readConfig();
    ensureSessionCacheVersion();
    weddingDateMemory = getPersistedWeddingDate();
    syncWeddingDateStore(weddingDateMemory);

    adoptInlineStep1();
    prioritizeStep1Video();
    prioritizeStep2Video();
    showStepElement(1);
    bindStepActions();
    bindProgressStepNavigation();
    bindVideoPrefetchOnNavigation();
    scheduleBackgroundVideoWarmup();

    requestAnimationFrame(function () {
      initGlobalLightboxBindings();
      initGlobalImageZoomCapture();
    });

    window.addEventListener('resize', function () {
      updateProgressUI(currentStep);
    });

    window.QV2 = {
      showStep: showStep,
      getCurrentStep: function () { return currentStep; },
      getConfig: function () { return config; },
      getWeddingDate: getWeddingDate,
      getMinimumWeddingDate: getMinimumWeddingDate,
      isWeddingDateWithinAllowedRange: isWeddingDateWithinAllowedRange,
      captureWeddingDateSnapshot: captureWeddingDateSnapshot,
      forceFlushWeddingDateInput: forceFlushWeddingDateInput,
      setWeddingDate: setPersistedWeddingDate,
      openImageLightbox: openImageLightbox,
      closeImageLightbox: closeImageLightbox,
      ensureSharedUiInBody: ensureSharedUiInBody,
      resolveZoomImageSrc: resolveZoomImageSrc,
      adoptInlineSteps: adoptInlineStepsForSection,
      reloadStepSection: reloadStepSection,
      forceLoadImage: forceLoadImage,
      fixBrokenImagesInStep: fixBrokenImagesInStep,
      hydrateStepImages: hydrateStepImages,
      bindImageErrorRecovery: bindImageErrorRecovery,
      clearSessionCache: function () {
        try {
          Object.keys(sessionStorage).forEach(function (key) {
            if (key.indexOf('qv2:sess:') === 0) sessionStorage.removeItem(key);
          });
        } catch (e) {}
      }
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
